/**
 * useImageLoad — flip a flag when the given image finishes decoding.
 *
 * Used by `RevealedImage` and `AtmosphereSurface` to coordinate the LQIP
 * → full-image fade so the card never reads as "empty."
 *
 * Two-mode design:
 *
 * 1. **Ref mode** (`useImageLoadRef`): attach the returned ref to the
 *    real `<img>` element. We listen for that element's own `load`
 *    event, so the LQIP fades out exactly when the bytes the browser
 *    chose (typically the AVIF source in our `<picture>`) finish
 *    decoding. This avoids the failure mode where preloading the PNG
 *    fallback via `new Image()` doubles the bytes-on-wire.
 *
 * 2. **Cache mode** (`useImageLoaded`): a pure cache lookup for callers
 *    that already know an image is loaded (e.g., switching between
 *    themes that share a previously-loaded image).
 *
 * The original cache-only API (`useImageLoad`) preloaded via
 * `new Image()`, which sent a second request alongside the `<picture>`
 * source the browser already picked. Removed to fix that.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LOADED_CACHE = new Set<string>();

/** Reset the per-src cache. Test-only. */
export function _resetLoadCacheForTests(): void {
  LOADED_CACHE.clear();
}

export interface ImageLoadRef {
  /** Attach to the real <img> element. */
  ref: (el: HTMLImageElement | null) => void;
  /** Has the element fired `load` since mount (or was it already complete)? */
  loaded: boolean;
}

/**
 * Returned ref + flag pair. Attach the ref to the `<img>` element you
 * care about; `loaded` flips true once that element has finished
 * decoding (or was already complete on mount). Keyed by `src` so theme
 * swaps don't replay the fade.
 */
export function useImageLoadRef(src: string | undefined): ImageLoadRef {
  const cached = src ? LOADED_CACHE.has(src) : false;
  const [loaded, setLoaded] = useState(cached);
  const elRef = useRef<HTMLImageElement | null>(null);

  // Reset when the src changes — but consult the cache first so a
  // re-mount of an already-loaded image doesn't replay the fade.
  useEffect(() => {
    if (!src) {
      setLoaded(false);
      return;
    }
    if (LOADED_CACHE.has(src)) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    const el = elRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      LOADED_CACHE.add(src);
      setLoaded(true);
    }
  }, [src]);

  const ref = useCallback(
    (el: HTMLImageElement | null) => {
      elRef.current = el;
      if (!el || !src) return;
      if (el.complete && el.naturalWidth > 0) {
        LOADED_CACHE.add(src);
        setLoaded(true);
        return;
      }
      const onLoad = (): void => {
        LOADED_CACHE.add(src);
        setLoaded(true);
        el.removeEventListener('load', onLoad);
        el.removeEventListener('error', onError);
      };
      const onError = (): void => {
        // Never get stuck on a broken URL — drop the LQIP veil so the
        // user at least sees whatever did render.
        LOADED_CACHE.add(src);
        setLoaded(true);
        el.removeEventListener('load', onLoad);
        el.removeEventListener('error', onError);
      };
      el.addEventListener('load', onLoad);
      el.addEventListener('error', onError);
    },
    [src],
  );

  return { ref, loaded };
}

/** Cache lookup only — no preload, no DOM listening. */
export function useImageLoaded(src: string | undefined): boolean {
  const initial = src ? LOADED_CACHE.has(src) : false;
  const [loaded, setLoaded] = useState(initial);
  useEffect(() => {
    if (src && LOADED_CACHE.has(src)) setLoaded(true);
  }, [src]);
  return loaded;
}

/**
 * @deprecated Preloaded the canonical PNG, doubling bytes on wire
 * alongside a `<picture>`'s AVIF source. Use `useImageLoadRef` so the
 * load signal comes from the real DOM `<img>` that the browser picked.
 */
export function useImageLoad(src: string | undefined): boolean {
  return useImageLoaded(src);
}
